/**
 * Minimal WAV encoder. Takes an AudioBuffer, returns a Blob with a
 * 16-bit PCM mono WAV file. Mono keeps the file ~half the size at the
 * cost of losing stereo placement, which is fine for sketches and
 * keeps Telegram uploads small.
 */

const HOST_IS_LE = (() => {
  const probe = new Uint16Array([0x0102]);
  return new Uint8Array(probe.buffer)[0] === 0x02;
})();

export function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = 1; // force mono mixdown
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  const bytesPerSample = 2; // 16-bit
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = length * blockAlign;

  const channelData = mergeToMono(buffer);

  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  // fmt chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  // data chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  if (HOST_IS_LE) {
    const pcm = new Int16Array(arrayBuffer, 44, length);
    for (let i = 0; i < length; i++) {
      const s = channelData[i];
      const clamped = s < -1 ? -1 : s > 1 ? 1 : s;
      pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }
  } else {
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const s = channelData[i];
      const clamped = s < -1 ? -1 : s > 1 ? 1 : s;
      view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function mergeToMono(buffer: AudioBuffer): Float32Array {
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0);
  const result = new Float32Array(buffer.length);
  const inv = 1 / buffer.numberOfChannels;
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < buffer.length; i++) {
      result[i] += data[i] * inv;
    }
  }
  return result;
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
